import * as api from "../../lib/api.js";
import type { AccountSessionOut, WalletOut } from "../contract/api";
import { useApp } from "../state/store";

/**
 * Home's two reads -- the wallet and the recent meetings -- started while the launch is
 * still covering the screen (#1066).
 *
 * Boot knows the stored sign-in is good before Home exists, and these two requests are
 * the slowest thing on the first screen after it: started then, beside the device link,
 * Home usually arrives filled instead of showing skeletons. They are **outside the boot
 * wait**: boot never awaits them, so the rotation cap, the failure rule and Try again are
 * unchanged, and a slow or failed read is shown by the screen exactly as before.
 *
 * Shared, not cached. A screen that mounts while a read is in flight or fresh uses it
 * instead of asking again; past `FRESH_MS` it asks the server itself, because a balance a
 * meeting has since spent is the one thing a stale read must not show. Each read belongs
 * to the account it was started for, so a different sign-in in the same tab never sees
 * it. Shared rather than taken once because React's development double-mount would take
 * it on the first pass and ask again on the second.
 */

/** How long a launch read stays this visit's answer. */
export const FRESH_MS = 30_000;

/**
 * The recent strip's rows: the read's limit, and the rows its skeleton draws (#1049).
 *
 * Here rather than in the screen because the launch starts the same read, and the limit
 * the skeleton promises has to be the limit the read asked for.
 */
export const RECENT_LIMIT = 2;

interface LaunchRead<T> {
  accountId: string;
  startedAt: number;
  read: Promise<T>;
}

let wallet: LaunchRead<WalletOut> | null = null;
let recent: LaunchRead<AccountSessionOut[]> | null = null;

/**
 * Keep the rejection for whoever uses the read, while marking it handled here.
 *
 * Boot starts these before it knows whether Home will follow: a deep link straight to a
 * result never mounts it, and a rejection nobody took reaches `unhandledrejection` in
 * `main.tsx`, which would put "Something broke" over the page for a read nobody wanted.
 * The original promise still rejects for a screen that does use it.
 */
function handled<T>(read: Promise<T>): Promise<T> {
  void read.catch(() => {});
  return read;
}

function usable<T>(entry: LaunchRead<T> | null, now: number): Promise<T> | null {
  const accountId = useApp.getState().account?.account_id;
  if (!entry || !accountId || entry.accountId !== accountId) return null;
  return now - entry.startedAt < FRESH_MS ? entry.read : null;
}

/**
 * Start both reads for this account. Boot calls it once `/me` has confirmed the stored
 * sign-in; a second call while they are still fresh asks nothing.
 */
export function startHomeData(accountId: string, now = Date.now()): void {
  if (wallet?.accountId === accountId && now - wallet.startedAt < FRESH_MS) return;
  wallet = { accountId, startedAt: now, read: handled(api.wallet()) };
  recent = {
    accountId,
    startedAt: now,
    read: handled(api.accountSessions({ limit: RECENT_LIMIT }) as Promise<AccountSessionOut[]>),
  };
}

/** The launch's wallet read, while it is this account's and fresh; null otherwise. */
export function launchWalletRead(now = Date.now()): Promise<WalletOut> | null {
  return usable(wallet, now);
}

/** The launch's recent-meetings read, while it is this account's and fresh; null otherwise. */
export function launchRecentRead(now = Date.now()): Promise<AccountSessionOut[]> | null {
  return usable(recent, now);
}

/** Drop both, so the next screen asks the server. For tests, and a wallet `reload`. */
export function forgetHomeData(): void {
  wallet = null;
  recent = null;
}
